'use client';

import React, { useMemo, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Image from 'next/image';
import Link from 'next/link';
import { Bird, Layers, Home, Wheat, Egg, Skull, ShoppingBag, DollarSign, AlertTriangle, AlertCircle, CheckCircle2, Clock, Circle, ArrowRight, ArrowUpRight, ArrowDownRight, RefreshCw, ChevronRight } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line } from 'recharts';
import { DashboardDataProvider, useDashboardData } from './components/DashboardDataContext';
import Icon from '@/components/ui/AppIcon';


// ─── Color tokens ─────────────────────────────────────────────────────────────
const GREEN = '#22c55e';
const GREEN_DARK = '#16a34a';
const GREEN_BG = 'rgba(34,197,94,0.12)';
const GOLD = '#d97706';
const GOLD_BG = 'rgba(217,119,6,0.12)';
const DANGER = '#ef4444';
const DANGER_BG = 'rgba(239,68,68,0.12)';
const CARD_BG = '#111827';
const CARD_BORDER = '#1f2937';
const TEXT = '#f9fafb';
const MUTED = '#9ca3af';

// ─── KPI Data ─────────────────────────────────────────────────────────────────
const KPI_DATA = [
  { id: 'k1', label: 'Total Live Birds', value: '84,320', sub: '6 active flocks', change: 2.4, icon: Bird, color: GREEN, href: '/flock-management' },
  { id: 'k2', label: 'Eggs Today', value: '18,640', sub: '93.2% HDP', change: 3.1, icon: Egg, color: GREEN, href: '/egg-production' },
  { id: 'k3', label: 'Feed in Stock', value: '4.2 days', sub: '6,840 kg left', change: -18.3, icon: Wheat, color: GOLD, href: '/inventory-stores', invert: true },
  { id: 'k4', label: 'Mortality Today', value: '47 birds', sub: '0.056% of flock', change: 34.3, icon: Skull, color: DANGER, href: '/mortality-culling', invert: true },
  { id: 'k5', label: 'Active Flocks', value: '6', sub: '2 due soon', change: 0, icon: Layers, color: '#60a5fa', href: '/flock-management' },
  { id: 'k6', label: 'Active Houses', value: '8 / 12', sub: '4 available', change: 0, icon: Home, color: '#a78bfa', href: '/houses' },
  { id: 'k7', label: 'Birds Sold (MTD)', value: '12,800', sub: '3 orders', change: 8.5, icon: ShoppingBag, color: GREEN, href: '/sales' },
  { id: 'k8', label: 'Revenue (MTD)', value: 'UGX 38.4M', sub: 'Target: 42M', change: -8.6, icon: DollarSign, color: GOLD, href: '/expenses', invert: true },
];

// ─── Chart Data ───────────────────────────────────────────────────────────────
const EGG_TREND_DATA = [
  { day: '28 Aug', eggs: 17200, target: 17500 },
  { day: '29 Aug', eggs: 17840, target: 17500 },
  { day: '30 Aug', eggs: 18100, target: 17500 },
  { day: '31 Aug', eggs: 17650, target: 17500 },
  { day: '01 Sep', eggs: 18320, target: 17500 },
  { day: '02 Sep', eggs: 18490, target: 17500 },
  { day: '03 Sep', eggs: 18640, target: 17500 },
];

const BIRD_POPULATION = [
  { name: 'Broilers', value: 38400, color: '#f97316', pct: '45.5%' },
  { name: 'Layers', value: 32000, color: '#22c55e', pct: '38.0%' },
  { name: 'Breeders', value: 10800, color: '#60a5fa', pct: '12.8%' },
  { name: 'Local Birds', value: 3120, color: GOLD, pct: '3.7%' },
];

const FEED_DATA = [
  { day: '28 Aug', consumed: 1580, target: 1600 },
  { day: '29 Aug', consumed: 1610, target: 1600 },
  { day: '30 Aug', consumed: 1620, target: 1600 },
  { day: '31 Aug', consumed: 1595, target: 1600 },
  { day: '01 Sep', consumed: 1640, target: 1600 },
  { day: '02 Sep', consumed: 1655, target: 1600 },
  { day: '03 Sep', consumed: 1640, target: 1600 },
];

// ─── Farm Performance Table Data ──────────────────────────────────────────────
const FARM_TABLE = [
  { house: 'House A1', flock: 'BR-2026-001', birds: 14200, eggs: 0, feed: 284, mortality: 8, fcr: '1.82', status: 'good' },
  { house: 'House A2', flock: 'BR-2026-002', birds: 12800, eggs: 0, feed: 256, mortality: 12, fcr: '1.91', status: 'good' },
  { house: 'Layer 01', flock: 'LY-2026-001', birds: 9600, eggs: 8840, feed: 192, mortality: 6, fcr: '—', status: 'good' },
  { house: 'Layer 02', flock: 'LY-2026-002', birds: 8800, eggs: 8120, feed: 176, mortality: 11, fcr: '—', status: 'warning' },
  { house: 'House B1', flock: 'BR-2026-003', birds: 11200, eggs: 0, feed: 224, mortality: 7, fcr: '1.78', status: 'good' },
  { house: 'House B2', flock: 'LY-2026-003', birds: 7200, eggs: 1680, feed: 144, mortality: 9, fcr: '—', status: 'danger' },
];

// ─── Tasks ────────────────────────────────────────────────────────────────────
const TASKS = [
  { id: 't1', title: 'Morning feeding — House A1 & A2', status: 'completed', time: '06:00', assignee: 'Ogenga Paul' },
  { id: 't2', title: 'Egg collection — Layer House 01', status: 'completed', time: '06:30', assignee: 'Akello Grace' },
  { id: 't3', title: 'Weight sampling — Flock BR-2026-002', status: 'in_progress', time: '08:00', assignee: 'Ogenga Paul' },
  { id: 't4', title: 'Footbath change — House B1 & B2', status: 'overdue', time: '09:00', assignee: 'Apio Christine' },
  { id: 't5', title: 'Newcastle vaccination — LY-2026-003', status: 'pending', time: '10:00', assignee: 'Dr. Nakato Sarah' },
  { id: 't6', title: 'Midday feeding — all broiler houses', status: 'pending', time: '12:00', assignee: 'Ogenga Paul' },
];

// ─── Stock Alerts ─────────────────────────────────────────────────────────────
const STOCK_ALERTS = [
  { id: 's1', item: 'Broiler Starter Feed', current: 840, unit: 'kg', daysLeft: 3.1, severity: 'critical' },
  { id: 's2', item: 'Newcastle Vaccine (La Sota)', current: 12, unit: 'doses×1000', daysLeft: null, severity: 'critical' },
  { id: 's3', item: 'Layers Mash', current: 1840, unit: 'kg', daysLeft: 4.6, severity: 'low' },
  { id: 's4', item: 'Enrofloxacin 10%', current: 3, unit: 'bottles', daysLeft: null, severity: 'low' },
  { id: 's5', item: 'Egg Trays (30-cell)', current: 240, unit: 'trays', daysLeft: null, severity: 'low' },
];

// ─── Tooltip helpers ──────────────────────────────────────────────────────────
function DarkTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', minWidth: 140 }}>
      <p style={{ color: TEXT, fontSize: 11, fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 3 }}>
          <span style={{ color: MUTED, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: p.color, display: 'inline-block' }} />
            {p.name}
          </span>
          <span style={{ color: TEXT, fontSize: 11, fontWeight: 700 }}>{p.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function BannerCard() {
  return (
    <div className="relative rounded-2xl overflow-hidden mb-5" style={{ minHeight: 160 }}>
      <Image
        src="/assets/images/bg2-1788775741867.png"
        alt="Y77 Poultry Farm aerial view showing farm buildings and operations"
        fill
        className="object-cover"
        priority
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.15) 100%)' }} />
      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: GREEN }} />
            <span style={{ color: GREEN, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>LIVE OPERATIONS</span>
          </div>
          <h1 style={{ color: TEXT, fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>Y77 Poultry Farm</h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, marginTop: 4 }}>Idudi Farm · 6 active flocks · 84,320 birds under management</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="rounded-xl px-4 py-3 text-center" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', minWidth: 90 }}>
            <p style={{ color: GREEN, fontSize: 20, fontWeight: 800 }}>18,640</p>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>Eggs Today</p>
          </div>
          <div className="rounded-xl px-4 py-3 text-center" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', minWidth: 90 }}>
            <p style={{ color: GOLD, fontSize: 20, fontWeight: 800 }}>93.2%</p>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>Hen Day Prod.</p>
          </div>
          <div className="rounded-xl px-4 py-3 text-center" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', minWidth: 90 }}>
            <p style={{ color: '#60a5fa', fontSize: 20, fontWeight: 800 }}>UGX 38.4M</p>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>Revenue MTD</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCards() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3 mb-5">
      {KPI_DATA.map((kpi) => {
        const Icon = kpi.icon;
        const isUp = kpi.change > 0;
        const isDown = kpi.change < 0;
        const trendGood = kpi.invert ? isDown : isUp;
        const trendBad = kpi.invert ? isUp : isDown;
        const trendColor = kpi.change === 0 ? MUTED : trendGood ? GREEN : DANGER;
        return (
          <Link key={kpi.id} href={kpi.href} className="block">
            <div
              className="rounded-xl p-3 h-full hover:scale-[1.02] transition-transform duration-150"
              style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${kpi.color}20` }}>
                  <Icon size={13} style={{ color: kpi.color }} />
                </div>
                {kpi.change !== 0 && (
                  <div className="flex items-center gap-0.5" style={{ color: trendColor, fontSize: 10, fontWeight: 700 }}>
                    {trendGood ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                    {Math.abs(kpi.change)}%
                  </div>
                )}
              </div>
              <p style={{ color: TEXT, fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>{kpi.value}</p>
              <p style={{ color: MUTED, fontSize: 10, marginTop: 2, lineHeight: 1.3 }}>{kpi.label}</p>
              <p style={{ color: '#6b7280', fontSize: 10, marginTop: 1 }}>{kpi.sub}</p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function EggProductionTrendChart() {
  return (
    <div className="rounded-xl p-4" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 style={{ color: TEXT, fontSize: 13, fontWeight: 700 }}>Egg Production Trend</h3>
          <p style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>Last 7 days — Layer Houses</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5" style={{ color: MUTED, fontSize: 11 }}>
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: GREEN }} />Actual
          </span>
          <span className="flex items-center gap-1.5" style={{ color: MUTED, fontSize: 11 }}>
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: GOLD }} />Target
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={EGG_TREND_DATA} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="eggGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={GREEN} stopOpacity={0.25} />
              <stop offset="95%" stopColor={GREEN} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false} />
          <Tooltip content={<DarkTooltip />} />
          <Area type="monotone" dataKey="eggs" name="Eggs" stroke={GREEN} strokeWidth={2} fill="url(#eggGrad)" dot={{ fill: GREEN, r: 3, strokeWidth: 0 }} />
          <Line type="monotone" dataKey="target" name="Target" stroke={GOLD} strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function BirdPopulationChart() {
  return (
    <div className="rounded-xl p-4 flex flex-col" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
      <div className="mb-3">
        <h3 style={{ color: TEXT, fontSize: 13, fontWeight: 700 }}>Bird Population</h3>
        <p style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>84,320 total live birds</p>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <PieChart>
          <Pie data={BIRD_POPULATION} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} dataKey="value">
            {BIRD_POPULATION.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0];
              return (
                <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: '8px 12px' }}>
                  <p style={{ color: TEXT, fontSize: 11, fontWeight: 600 }}>{d.name}</p>
                  <p style={{ color: GREEN, fontSize: 13, fontWeight: 700 }}>{(d.value as number).toLocaleString()}</p>
                  <p style={{ color: MUTED, fontSize: 10 }}>{(d.payload as { pct: string }).pct} of total</p>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {BIRD_POPULATION.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
            <div>
              <p style={{ color: TEXT, fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>{item.name}</p>
              <p style={{ color: MUTED, fontSize: 10 }}>{item.value.toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeedConsumptionChart() {
  return (
    <div className="rounded-xl p-4" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 style={{ color: TEXT, fontSize: 13, fontWeight: 700 }}>Feed Consumption</h3>
          <p style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>Daily kg — last 7 days</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5" style={{ color: MUTED, fontSize: 11 }}>
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: GOLD }} />Consumed
          </span>
          <span className="flex items-center gap-1.5" style={{ color: MUTED, fontSize: 11 }}>
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#374151' }} />Target
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={FEED_DATA} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={18}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: MUTED }} axisLine={false} tickLine={false} />
          <Tooltip content={<DarkTooltip />} />
          <Bar dataKey="target" name="Target" fill="#374151" radius={[3, 3, 0, 0]} />
          <Bar dataKey="consumed" name="Consumed" fill={GOLD} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function FarmPerformanceTable() {
  const statusMap: Record<string, { color: string; label: string }> = {
    good: { color: GREEN, label: 'Good' },
    warning: { color: GOLD, label: 'Watch' },
    danger: { color: DANGER, label: 'Alert' },
  };
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
        <div>
          <h3 style={{ color: TEXT, fontSize: 13, fontWeight: 700 }}>Farm Performance</h3>
          <p style={{ color: MUTED, fontSize: 11, marginTop: 1 }}>Today's summary by house</p>
        </div>
        <Link href="/houses" style={{ color: GREEN, fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
          View all <ChevronRight size={12} />
        </Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              {['House', 'Flock', 'Birds', 'Eggs', 'Feed (kg)', 'Mortality', 'FCR', 'Status'].map((h) => (
                <th key={h} className="px-4 py-2.5 text-left font-semibold" style={{ color: MUTED, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FARM_TABLE.map((row, i) => {
              const s = statusMap[row.status];
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${CARD_BORDER}` }} className="hover:bg-white/5 transition-colors">
                  <td className="px-4 py-2.5 font-semibold" style={{ color: TEXT }}>{row.house}</td>
                  <td className="px-4 py-2.5" style={{ color: MUTED }}>{row.flock}</td>
                  <td className="px-4 py-2.5 font-tabular" style={{ color: TEXT }}>{row.birds.toLocaleString()}</td>
                  <td className="px-4 py-2.5 font-tabular" style={{ color: row.eggs > 0 ? GREEN : MUTED }}>{row.eggs > 0 ? row.eggs.toLocaleString() : '—'}</td>
                  <td className="px-4 py-2.5 font-tabular" style={{ color: GOLD }}>{row.feed}</td>
                  <td className="px-4 py-2.5 font-tabular" style={{ color: row.mortality > 10 ? DANGER : TEXT }}>{row.mortality}</td>
                  <td className="px-4 py-2.5 font-tabular" style={{ color: MUTED }}>{row.fcr}</td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: `${s.color}20`, color: s.color, fontSize: 10 }}>
                      {s.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UpcomingTasksPanel() {
  const statusCfg: Record<string, { icon: React.ElementType; color: string }> = {
    completed: { icon: CheckCircle2, color: GREEN },
    in_progress: { icon: Clock, color: '#60a5fa' },
    overdue: { icon: AlertCircle, color: DANGER },
    pending: { icon: Circle, color: MUTED },
  };
  const done = TASKS.filter((t) => t.status === 'completed').length;
  const pct = Math.round((done / TASKS.length) * 100);
  return (
    <div className="rounded-xl overflow-hidden flex flex-col" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
        <div>
          <h3 style={{ color: TEXT, fontSize: 13, fontWeight: 700 }}>Upcoming Tasks</h3>
          <p style={{ color: MUTED, fontSize: 11, marginTop: 1 }}>{done}/{TASKS.length} completed today</p>
        </div>
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: pct >= 75 ? GREEN_DARK : pct >= 50 ? GOLD : DANGER, fontSize: 11 }}>
          {pct}%
        </div>
      </div>
      <div className="px-4 pt-2.5 pb-1">
        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#1f2937' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: pct >= 75 ? GREEN : pct >= 50 ? GOLD : DANGER }} />
        </div>
      </div>
      <div className="divide-y flex-1 overflow-y-auto" style={{ maxHeight: 260, borderColor: CARD_BORDER }}>
        {TASKS.map((task) => {
          const cfg = statusCfg[task.status] ?? statusCfg.pending;
          const StatusIcon = cfg.icon;
          return (
            <div key={task.id} className="flex items-start gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors">
              <StatusIcon size={13} className="flex-shrink-0 mt-0.5" style={{ color: cfg.color }} />
              <div className="flex-1 min-w-0">
                <p style={{ color: TEXT, fontSize: 11, lineHeight: 1.4, textDecoration: task.status === 'completed' ? 'line-through' : 'none', opacity: task.status === 'completed' ? 0.5 : 1 }} className="truncate">
                  {task.title}
                </p>
                <p style={{ color: MUTED, fontSize: 10, marginTop: 2 }}>{task.time} · {task.assignee}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-4 py-2.5" style={{ borderTop: `1px solid ${CARD_BORDER}` }}>
        <Link href="/tasks" className="flex items-center gap-1 text-xs font-semibold" style={{ color: GREEN }}>
          View full board <ArrowRight size={11} />
        </Link>
      </div>
    </div>
  );
}

function LowStockPanel() {
  const critical = STOCK_ALERTS.filter((s) => s.severity === 'critical').length;
  return (
    <div className="rounded-xl overflow-hidden flex flex-col" style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
        <div>
          <h3 style={{ color: TEXT, fontSize: 13, fontWeight: 700 }}>Low Stock Alerts</h3>
          <p style={{ color: MUTED, fontSize: 11, marginTop: 1 }}>{critical} critical items</p>
        </div>
        <AlertTriangle size={15} style={{ color: GOLD }} />
      </div>
      <div className="divide-y flex-1 overflow-y-auto" style={{ maxHeight: 280, borderColor: CARD_BORDER }}>
        {STOCK_ALERTS.map((s) => (
          <div key={s.id} className="flex items-start gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors">
            <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: s.severity === 'critical' ? DANGER_BG : GOLD_BG }}>
              {s.severity === 'critical'
                ? <AlertCircle size={12} style={{ color: DANGER }} />
                : <AlertTriangle size={12} style={{ color: GOLD }} />}
            </div>
            <div className="flex-1 min-w-0">
              <p style={{ color: TEXT, fontSize: 11, fontWeight: 600, lineHeight: 1.3 }} className="truncate">{s.item}</p>
              <p style={{ color: MUTED, fontSize: 10, marginTop: 2 }}>
                {s.current} {s.unit} remaining
                {s.daysLeft && <span style={{ color: s.severity === 'critical' ? DANGER : GOLD, fontWeight: 700 }}> · ~{s.daysLeft}d left</span>}
              </p>
            </div>
            <span className="px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0" style={{ backgroundColor: s.severity === 'critical' ? DANGER_BG : GOLD_BG, color: s.severity === 'critical' ? DANGER : GOLD, fontSize: 9 }}>
              {s.severity === 'critical' ? 'CRITICAL' : 'LOW'}
            </span>
          </div>
        ))}
      </div>
      <div className="px-4 py-2.5" style={{ borderTop: `1px solid ${CARD_BORDER}` }}>
        <Link href="/inventory-stores" className="text-xs font-semibold" style={{ color: GREEN }}>
          View inventory →
        </Link>
      </div>
    </div>
  );
}

// ─── Dashboard Header ─────────────────────────────────────────────────────────
function DashboardHeader() {
  const { lastUpdatedLabel, isRefreshing, refresh } = useDashboardData();
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <div>
        <h1 style={{ color: TEXT, fontSize: 20, fontWeight: 800 }}>Operations Dashboard</h1>
        <p style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>Monitor your farm performance and operations in real-time.</p>
      </div>
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: GREEN_BG, color: GREEN }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GREEN, animation: isRefreshing ? 'none' : 'pulse 2s infinite' }} />
          {isRefreshing ? 'Refreshing…' : 'Live data'}
        </span>
        <span style={{ color: MUTED, fontSize: 11 }}>Updated {lastUpdatedLabel}</span>
        <button
          onClick={refresh}
          disabled={isRefreshing}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-opacity"
          style={{ backgroundColor: '#1f2937', color: MUTED, opacity: isRefreshing ? 0.5 : 1 }}
        >
          <RefreshCw size={11} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function OperationsDashboardPage() {
  return (
    <AppLayout>
      <DashboardDataProvider>
        <div className="px-4 md:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto">
          <DashboardHeader />

          {/* Banner with bg2 image */}
          <BannerCard />

          {/* Compact KPI Cards */}
          <KpiCards />

          {/* Charts row: Egg trend (2/3) + Bird population (1/3) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2">
              <EggProductionTrendChart />
            </div>
            <div>
              <BirdPopulationChart />
            </div>
          </div>

          {/* Feed consumption chart */}
          <div className="mb-4">
            <FeedConsumptionChart />
          </div>

          {/* Farm performance table */}
          <div className="mb-4">
            <FarmPerformanceTable />
          </div>

          {/* Tasks + Low stock */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UpcomingTasksPanel />
            <LowStockPanel />
          </div>
        </div>
      </DashboardDataProvider>
    </AppLayout>
  );
}